import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import {
  AureliaBootstrap,
  AureliaInstance,
  AureliaLoaderConfig,
  AureliaModule,
  DeferredPromise,
  IAureliaLoader,
  ManifestData,
  TYPES,
} from './aurelia-loader.types';

@injectable()
export class AureliaLoader implements IAureliaLoader {
  private systemDeferred: DeferredPromise<AureliaBootstrap> | null = null;
  private appVersion: string | null = null;

  constructor(
    @inject(TYPES.AureliaLoaderConfig)
    private readonly config: AureliaLoaderConfig
  ) {}

  public async loadAureliaLibraries(container: Element): Promise<void> {
    const moduleDeferred = this.generateDeferredPromise<void>();

    // Load manifest to get appVersion if not provided in config
    if (!this.appVersion) {
      try {
        const manifest = await this.loadManifest();

        this.appVersion = manifest.appVersion;
      } catch (error) {
        console.error('Failed to load manifest for appVersion:', error);
        moduleDeferred.reject(error);
        return moduleDeferred.promise;
      }
    }

    if (this.systemDeferred) {
      this.systemDeferred.promise.then((bootstrap) =>
        this.initializeModule(bootstrap, container, moduleDeferred)
      );
    } else {
      this.systemDeferred = this.generateDeferredPromise<AureliaBootstrap>();
      await this.loadSystemJs(container, moduleDeferred);
    }

    return moduleDeferred.promise;
  }

  public async loadManifest(): Promise<ManifestData> {
    const manifestUrl = this.getManifestUrl();

    try {
      const response = await fetch(manifestUrl);

      if (!response.ok) {
        throw new Error(`Failed to load manifest: ${response.status} ${response.statusText}`);
      }

      const manifestData = (await response.json()) as ManifestData;

      return manifestData;
    } catch (error) {
      console.error('Failed to load manifest:', error);
      throw error;
    }
  }

  private async loadSystemJs(
    container: Element,
    moduleDeferred: DeferredPromise<void>
  ): Promise<void> {
    const baseUrl = this.config.baseUrl;
    const appVersion = this.appVersion;

    if (!appVersion) {
      const error = new Error('No appVersion available from config or manifest');

      console.error(error.message);
      moduleDeferred.reject(error);
      return;
    }

    const versionSlug = appVersion.replace(/\//g, '-').replace(/\./g, '_');

    try {
      await this.getScript(this.getSystemJsUrl('/vendor/system.js'));
      window.SystemJS.config({ baseURL: baseUrl });

      await this.getScript(this.getSystemJsUrl(`/bundles/desygnerbrand/config--${versionSlug}.js`));

      this.autoloadModule(container, moduleDeferred);
    } catch (error) {
      console.error('Failed to load SystemJS:', error);
      moduleDeferred.reject(error);
    }
  }

  private autoloadModule(container: Element, moduleDeferred: DeferredPromise<void>): void {
    window.SystemJS.import('aurelia-bootstrapper').then((aurelia: unknown) => {
      const aureliaBootstrapper = aurelia as { bootstrap: AureliaBootstrap };
      const observer = new MutationObserver(() => {
        if (this.systemDeferred) {
          this.systemDeferred.resolve(aureliaBootstrapper.bootstrap);
          moduleDeferred.resolve();
          observer.disconnect();
        }
      });

      observer.observe(container, { childList: true, subtree: true });
    });
  }

  private initializeModule(
    bootstrap: AureliaBootstrap,
    container: Element,
    moduleDeferred: DeferredPromise<void>
  ): void {
    const appHost = container;
    const moduleId = appHost.getAttribute('aurelia-app');

    if (!moduleId) {
      console.error('No aurelia-app attribute found on container');
      moduleDeferred.reject(new Error('No aurelia-app attribute found'));
      return;
    }

    window.SystemJS.import(moduleId).then((module: unknown) => {
      const aureliaModule = module as AureliaModule;

      bootstrap((aurelia: AureliaInstance) => {
        aurelia.host = appHost;
        aurelia.configModuleId = moduleId || null;

        aureliaModule.configure(aurelia);

        const observer = new MutationObserver(() => {
          moduleDeferred.resolve();
          observer.disconnect();
        });

        observer.observe(container, { childList: true, subtree: true });
      });
    });
  }

  private getSystemJsUrl(url: string): string {
    url = url.replace(/^\/+/, '');

    return this.config.useCdnUrl
      ? `https://static.desygner.com/assets/build/${url}`
      : `https://s3.amazonaws.com/assets.desygner.com/assets/build/${url}`;
  }

  private getManifestUrl(): string {
    // Map environment values to manifest path names
    const envMapping: Record<string, string> = {
      local: 'build', // for local testing, you can change this to any environment
      develop: 'dev',
      production: 'prod',
    };
    const env = envMapping[this.config.environment || ''] || this.config.environment || 'prod';

    return this.config.useCdnUrl
      ? `https://static.desygner.com/manifests/desygner/${env}/manifest.json`
      : `https://s3.amazonaws.com/assets.desygner.com/manifests/desygner/${env}/manifest.json`;
  }

  private async getScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = url;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Error loading ${url}`));

      document.body.appendChild(script);
    });
  }

  public generateDeferredPromise<T = unknown>(): DeferredPromise<T> {
    let resolveFunction!: (value: T | PromiseLike<T>) => void;
    let rejectFunction!: (reason?: unknown) => void;

    const promise = new Promise<T>((resolve, reject) => {
      resolveFunction = resolve;
      rejectFunction = reject;
    });

    return {
      promise,
      resolve: resolveFunction,
      reject: rejectFunction,
    };
  }
}
